import React, { useState, useRef } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { apiClient } from '../../services/api';
import logger from '../../utils/logger';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  label?: string;
  className?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  label = 'Obrázek',
  className,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation
    if (!file.type.startsWith('image/')) {
      setError('Vyberte prosím obrázek.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      // 10MB
      setError('Obrázek je příliš velký (max 10MB).');
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('photo', file);

    try {
      // Use relative path since proxy is likely set up in vite or we use same host
      // Only add Authorization header if we have a token
      const headers: Record<string, string> = {
        'Content-Type': 'multipart/form-data',
      };

      // Getting token from localStorage usually or context.
      // If axios interceptor is set up globally, we might not need this, but to be safe:
      const storedToken = localStorage.getItem('authToken');
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }

      const response = await apiClient.post<{ success?: boolean; url?: string }>('upload', formData, {
        headers,
      });

      if (response.success && response.url) {
        onChange(response.url);
      } else {
        setError('Nepodařilo se získat URL obrázku.');
      }
    } catch (err) {
      logger.error('Upload error', { error: err as Error });
      setError('Chyba při nahrávání obrázku.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    onChange('');
    setError(null);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="text-sm font-medium text-gray-700 dark:text-zinc-300">{label}</label>

      <div className="flex items-start gap-4">
        {value ? (
          <div className="relative group">
            <img
              src={value}
              alt="Preview"
              className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200 dark:border-zinc-700 shadow-sm"
            />
            <button
              type="button"
              onClick={handleRemove}
              aria-label="Odebrat obrázek"
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            className={`w-32 h-32 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors ${
              error
                ? 'border-red-300 bg-red-50 dark:bg-red-900/10 text-red-500'
                : 'border-gray-300 dark:border-zinc-600 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/10 text-gray-400 hover:text-green-500'
            }`}
          >
            {uploading ? <Loader2 className="animate-spin mb-2" /> : <Upload className="mb-2" />}
            <span className="text-xs font-medium">{uploading ? 'Nahrávám...' : 'Vybrat'}</span>
          </div>
        )}

        <div className="flex-1 space-y-2 text-sm text-gray-500">
          <p>Podporované formáty: JPG, PNG, WEBP. Max 10MB.</p>
          {error && <p className="text-red-500 text-xs">{error}</p>}
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        accept="image/*"
        aria-label={`${label} - výběr obrázku`}
      />
    </div>
  );
};
